import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import { useCurrentShopMember } from "@babackapp/private-api-client/client/use-current-shop-member";
import useOutgoingWebhookDeliveries, {
  useReactivateOutgoingWebhookEndpoint,
} from "@babackapp/private-api-client/client/use-outgoing-webhook-deliveries";
import useOutgoingWebhookSecret, {
  useRegenerateOutgoingWebhookSecret,
} from "@babackapp/private-api-client/client/use-outgoing-webhook-secret";
import useReturnWorkflowWebhooks from "@babackapp/private-api-client/client/use-return-workflow-webhooks";
import SettingsFormContainer, {
  SettingsFormHeader,
} from "@babackapp/react-hook-forms-components/form-page";
import useTranslation from "@babackapp/translations/use-translation";
import {
  WebhooksSettings,
  type WebhooksSettingsProps,
} from "@babackapp/workflows-editor/webhooks-settings";
import { Link } from "react-router-dom";

// The secret is the shop's, shared with tracking; the log shows the return
// deliveries only.
const useReturnWebhookDeliveries: WebhooksSettingsProps["api"]["useDeliveries"] =
  (shopId, options) =>
    useOutgoingWebhookDeliveries(shopId, {
      ...options,
      source: "RETURN_WORKFLOW",
    });

// The list is cast like the editor's return adapter: its items only differ
// from the tracking ones by the trigger type, which the page turns into a
// label.
const RETURN_WEBHOOKS_API: WebhooksSettingsProps["api"] = {
  useSecret: useOutgoingWebhookSecret,
  useRegenerateSecret: useRegenerateOutgoingWebhookSecret,
  useWebhooks:
    useReturnWorkflowWebhooks as unknown as WebhooksSettingsProps["api"]["useWebhooks"],
  useDeliveries: useReturnWebhookDeliveries,
  useReactivateEndpoint: useReactivateOutgoingWebhookEndpoint,
};

export default function ReturnWorkflowWebhooksPage() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const { member } = useCurrentShopMember(shop.id);
  const canManage = member?.roleId === "ADMIN" || member?.roleId === "OWNER";

  return (
    <SettingsFormContainer>
      <SettingsFormHeader
        title={t("sidebar.workflowWebhooks")}
        description={t("settings.workflowWebhooks.subtitle")}
      />
      <WebhooksSettings
        shopId={shop.id}
        canManage={canManage}
        api={RETURN_WEBHOOKS_API}
        docsUrl="https://docs.baback.co/returns/workflow-webhooks"
        renderWorkflowLink={(workflowId, label) => (
          <Link
            to={`/shops/${shop.id}/settings/notifications/workflows/${workflowId}`}
            className="text-sm font-medium hover:underline"
          >
            {label}
          </Link>
        )}
      />
    </SettingsFormContainer>
  );
}
